import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Disc3 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  getPlaylistItems,
  usePlaylistOptionsQuery,
  useReplacePlaylistItemsMutation,
  useReplaceTrackMetadataMutation,
  useTrackDetailQuery,
  type TrackCard,
  type TrackDetail,
} from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import { PlaybackButton } from '../../player/index.js';
import { Button } from '../../design-system/button.js';
import { Field, FieldLabel, Input } from '../../design-system/form.js';
import { StatusPill } from '../../design-system/status-pill.js';
import { Banner, Panel, StateSurface } from '../../design-system/surfaces.js';
import { DeliveryAction } from '../delivery/delivery-action.js';
import { FavoriteAction } from './favorite-action.js';
import {
  formatDuration,
  trackArtist,
  trackTitle,
  TrackSeams,
  useTrackPlaybackAction,
} from './track-presentation.js';

function MetadataForm({ track }: { track: TrackCard }) {
  const { library } = useWorkspace();
  const queryClient = useQueryClient();
  const mutation = useReplaceTrackMetadataMutation();
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [album, setAlbum] = useState('');

  useEffect(() => {
    setTitle(track.local_metadata?.title_override ?? '');
    setArtist(track.local_metadata?.artist_override ?? '');
    setAlbum(track.local_metadata?.album_override ?? '');
  }, [track.id, track.local_metadata]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await mutation.mutateAsync({
      libraryId: library.id,
      trackId: track.id,
      body: {
        title_override: title.trim() || null,
        artist_override: artist.trim() || null,
        album_override: album.trim() || null,
      },
    });
    await queryClient.invalidateQueries({ queryKey: ['track', track.id] });
    await queryClient.invalidateQueries({ queryKey: ['library-tracks', library.id] });
  };

  return (
    <Panel>
      <div className="mb-5">
        <p className="font-mono text-xs uppercase tracking-[0.14em] text-ink-3">Personal Library</p>
        <h2 className="mt-2 text-lg font-semibold text-ink">Edit song details</h2>
        <p className="mt-1 text-sm leading-6 text-ink-3">
          Changes apply only in Library; blank fields use provider metadata.
        </p>
      </div>
      <form className="grid gap-4" onSubmit={(event) => void submit(event)}>
        <Field>
          <FieldLabel htmlFor="metadata-title">Title</FieldLabel>
          <Input
            id="metadata-title"
            maxLength={512}
            onChange={(event) => setTitle(event.currentTarget.value)}
            placeholder="Use original title"
            value={title}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="metadata-artist">Artist</FieldLabel>
            <Input
              id="metadata-artist"
              maxLength={256}
              onChange={(event) => setArtist(event.currentTarget.value)}
              placeholder="No artist"
              value={artist}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="metadata-album">Album</FieldLabel>
            <Input
              id="metadata-album"
              maxLength={256}
              onChange={(event) => setAlbum(event.currentTarget.value)}
              placeholder="No album"
              value={album}
            />
          </Field>
        </div>
        {mutation.isError ? (
          <Banner title="Metadata could not be saved" tone="danger">
            Existing local metadata is unchanged.
          </Banner>
        ) : null}
        {mutation.isSuccess ? <Banner title="Song details saved" tone="success" /> : null}
        <div>
          <Button disabled={mutation.isPending} type="submit">
            {mutation.isPending ? 'Saving…' : 'Save metadata'}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function PlaylistOptions({ trackId }: { trackId: string }) {
  const { library } = useWorkspace();
  const queryClient = useQueryClient();
  const options = usePlaylistOptionsQuery(library.id, trackId, { limit: 100 });
  const replace = useReplacePlaylistItemsMutation();
  const [pendingId, setPendingId] = useState<string>();

  const toggle = async (playlistId: string, containsTrack: boolean) => {
    setPendingId(playlistId);
    try {
      const current = await getPlaylistItems(library.id, playlistId);
      const currentIds = current.items.map((item) => item.track_id);
      const nextIds = containsTrack
        ? currentIds.filter((id) => id !== trackId)
        : [...currentIds.filter((id) => id !== trackId), trackId];
      await replace.mutateAsync({
        libraryId: library.id,
        playlistId,
        body: { expected_revision: current.revision, track_ids: nextIds },
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['playlist-options', library.id, trackId] }),
        queryClient.invalidateQueries({ queryKey: ['playlist-items', library.id, playlistId] }),
        queryClient.invalidateQueries({ queryKey: ['playlist', library.id, playlistId] }),
        queryClient.invalidateQueries({ queryKey: ['playlists', library.id] }),
      ]);
    } finally {
      setPendingId(undefined);
    }
  };

  return (
    <Panel>
      <h2 className="text-lg font-semibold text-ink">Playlists</h2>
      <p className="mt-1 text-sm leading-6 text-ink-3">
        Choose which playlists include this track.
      </p>
      <div className="mt-5">
        {options.isPending ? (
          <StateSurface className="min-h-32" kind="loading" title="Loading playlists" />
        ) : options.isError ? (
          <StateSurface
            action={
              <Button onClick={() => void options.refetch()} size="small" variant="secondary">
                Try again
              </Button>
            }
            className="min-h-32"
            kind="error"
            title="Playlist membership could not be loaded"
          />
        ) : options.data.items.length === 0 ? (
          <StateSurface
            action={
              <Button asChild size="small" variant="secondary">
                <Link to="/playlists">Create a playlist</Link>
              </Button>
            }
            className="min-h-32"
            title="No playlists yet"
          />
        ) : (
          <div className="divide-y divide-border rounded-md border border-border">
            {options.data.items.map((option) => (
              <label
                className="flex min-h-12 cursor-pointer items-center gap-3 px-4 py-2 text-sm text-ink hover:bg-surface-hi"
                key={option.playlist.id}
              >
                <input
                  checked={option.contains_track}
                  className="size-4 accent-accent"
                  disabled={pendingId !== undefined}
                  onChange={() => void toggle(option.playlist.id, option.contains_track)}
                  type="checkbox"
                />
                <span className="min-w-0 flex-1 truncate">{option.playlist.name}</span>
                {pendingId === option.playlist.id ? (
                  <span className="font-mono text-xs text-accent">updating…</span>
                ) : null}
              </label>
            ))}
          </div>
        )}
        {replace.isError ? (
          <p className="mt-3 text-xs text-err" role="alert">
            Playlist membership changed elsewhere or could not be updated. Reload and try again.
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

type ProvenanceFact = { label: string; value: string | null; href: string | null };

function providerStrengthFacts(metadata: TrackDetail['provider_metadata']): ProvenanceFact[] {
  const model = metadata.model?.trim().toLowerCase() ?? '';
  const isV3 = /^v3(?:$|[-_.\s])/.test(model);
  const isV22 = /^v2\.2(?:$|[-_.\s])/.test(model);

  if (isV3) {
    return [
      {
        label: 'Style strength',
        value: metadata.style_strength === null ? null : String(metadata.style_strength),
        href: null,
      },
    ];
  }
  if (isV22) {
    return [
      {
        label: 'Lyrics & style strength',
        value: metadata.prompt_strength === null ? null : String(metadata.prompt_strength),
        href: null,
      },
    ];
  }
  return [
    {
      label: 'Provider prompt strength',
      value: metadata.prompt_strength === null ? null : String(metadata.prompt_strength),
      href: null,
    },
    {
      label: 'Provider style strength',
      value: metadata.style_strength === null ? null : String(metadata.style_strength),
      href: null,
    },
  ];
}

function ProviderProvenance({ track }: { track: TrackDetail }) {
  const metadata = track.provider_metadata;
  const generatedAt = track.provider_created_at
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(track.provider_created_at))
    : null;
  const facts: ProvenanceFact[] = [
    {
      label: 'Project',
      value: track.generation?.composer_project.title ?? null,
      href: track.generation ? `/projects/${track.generation.composer_project.id}` : null,
    },
    { label: 'Provider title', value: track.provider_title, href: null },
    { label: 'Provider project', value: track.provider_project?.title ?? null, href: null },
    { label: 'Model', value: metadata.model, href: null },
    { label: 'Generated', value: generatedAt, href: null },
    ...providerStrengthFacts(metadata),
  ];
  const visibleFacts = facts.filter(
    (fact): fact is { label: string; value: string; href: string | null } => fact.value !== null,
  );

  return (
    <Panel className="xl:col-span-2">
      <p className="font-mono text-xs uppercase tracking-[0.14em] text-ink-3">Song details</p>
      <dl className="mt-4 grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
        {visibleFacts.map((fact) => (
          <div key={fact.label}>
            <dt className="font-mono text-xs uppercase tracking-[0.1em] text-ink-3">
              {fact.label}
            </dt>
            <dd className="mt-1 break-words text-ink">
              {fact.href ? (
                <Link className="underline-offset-4 hover:underline" to={fact.href}>
                  {fact.value}
                </Link>
              ) : (
                fact.value
              )}
            </dd>
          </div>
        ))}
      </dl>

      {metadata.tags.length > 0 || metadata.negative_tags.length > 0 ? (
        <div className="mt-6 grid gap-5 border-t border-border pt-5 lg:grid-cols-2">
          {metadata.tags.length > 0 ? <ProviderTagList label="Tags" tags={metadata.tags} /> : null}
          {metadata.negative_tags.length > 0 ? (
            <ProviderTagList label="Excluded styles" tags={metadata.negative_tags} />
          ) : null}
        </div>
      ) : null}

      {metadata.prompt || metadata.lyrics ? (
        <div className="mt-6 grid gap-5 border-t border-border pt-5">
          {metadata.prompt ? <ProviderText label="Prompt" value={metadata.prompt} /> : null}
          {metadata.lyrics ? <ProviderText label="Final lyrics" value={metadata.lyrics} /> : null}
        </div>
      ) : null}
    </Panel>
  );
}

function ProviderTagList({ label, tags }: { label: string; tags: string[] }) {
  return (
    <section>
      <h2 className="font-mono text-xs uppercase tracking-[0.1em] text-ink-3">{label}</h2>
      <div className="mt-3 flex flex-wrap gap-2">
        {tags.map((tag) => (
          <span
            className="rounded-full border border-border bg-bg px-3 py-1 font-mono text-xs leading-5 text-ink-2"
            key={tag}
          >
            {tag}
          </span>
        ))}
      </div>
    </section>
  );
}

function ProviderText({ label, value }: { label: string; value: string }) {
  return (
    <section>
      <h2 className="font-mono text-xs uppercase tracking-[0.1em] text-ink-3">{label}</h2>
      <p className="mt-3 whitespace-pre-wrap break-words rounded-md border border-border bg-bg p-4 text-base leading-7 text-ink-2">
        {value}
      </p>
    </section>
  );
}

function ImportPanel({ track }: { track: TrackCard }) {
  const managedImport = track.library_import;
  return (
    <Panel>
      <p className="font-mono text-xs uppercase tracking-[0.14em] text-ink-3">Personal Library</p>
      {managedImport ? (
        <div className="mt-4 grid gap-4">
          <div className="flex flex-wrap gap-2">
            <StatusPill status={managedImport.status} />
            {managedImport.phase ? <StatusPill status={managedImport.phase} /> : null}
            {managedImport.asset ? <StatusPill status="available">owned WAV</StatusPill> : null}
          </div>
          {managedImport.asset ? (
            <dl className="grid gap-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="font-mono text-xs uppercase text-ink-3">Format</dt>
                <dd className="mt-1 text-ink">{managedImport.asset.format.toUpperCase()}</dd>
              </div>
              <div>
                <dt className="font-mono text-xs uppercase text-ink-3">Duration</dt>
                <dd className="mt-1 text-ink">
                  {formatDuration(managedImport.asset.duration_seconds)}
                </dd>
              </div>
              <div>
                <dt className="font-mono text-xs uppercase text-ink-3">Size</dt>
                <dd className="mt-1 text-ink">
                  {(managedImport.asset.byte_size / 1_048_576).toFixed(1)} MB
                </dd>
              </div>
            </dl>
          ) : null}
          {managedImport.failure ? (
            <Banner title="Library import failed" tone="danger">
              {managedImport.failure.message}
              {managedImport.failure.action_required ? (
                <span className="mt-2 block font-mono text-xs">
                  action required · {managedImport.failure.action_required}
                </span>
              ) : null}
            </Banner>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 text-sm leading-6 text-ink-3">
          Use Download WAV from the track menu to save it to your Personal Library.
        </p>
      )}
    </Panel>
  );
}

export function TrackDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { connection, library } = useWorkspace();
  const detail = useTrackDetailQuery(
    id,
    id ? { connection_id: connection.id, library_id: library.id } : undefined,
  );

  if (!id) {
    return <StateSurface kind="error" title="Track identity is missing" />;
  }
  if (detail.isPending) {
    return <StateSurface kind="loading" title="Loading track" />;
  }
  if (detail.isError) {
    return (
      <StateSurface
        action={
          <Button onClick={() => void detail.refetch()} size="small" variant="secondary">
            Try again
          </Button>
        }
        kind="error"
        title="Track could not be loaded"
      />
    );
  }

  return <TrackDetailContent track={detail.data} />;
}

function TrackDetailContent({ track }: { track: TrackDetail }) {
  const playback = useTrackPlaybackAction(track);
  const title = trackTitle(track);
  return (
    <article>
      <Link
        className="inline-flex min-h-11 items-center gap-2 rounded-md px-2 text-sm text-ink-2 outline-none hover:bg-surface hover:text-ink focus-visible:ring-2 focus-visible:ring-accent-ring"
        to={track.library_import ? '/library' : '/catalog'}
      >
        <ArrowLeft aria-hidden="true" className="size-4" /> Back
      </Link>

      <header className="mt-4 flex flex-col gap-5 border-b border-border pb-7 sm:flex-row sm:items-center">
        <div className="grid size-20 shrink-0 place-items-center rounded-lg border border-border bg-[linear-gradient(135deg,var(--surface-hi),var(--bg-2))] text-ink-3">
          <Disc3 aria-hidden="true" className="size-8" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-accent">
            Track
          </p>
          <h1 className="mt-2 break-words text-3xl font-semibold tracking-[-0.04em] text-ink sm:text-4xl">
            {title}
          </h1>
          <p className="mt-2 text-sm text-ink-2">
            {trackArtist(track)} · {formatDuration(track.duration_seconds)}
          </p>
          <div className="mt-3">
            <TrackSeams track={track} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {playback ? (
            <PlaybackButton
              control={playback}
              showLabel
              unavailableLabel={`Playback unavailable for ${title}`}
            />
          ) : null}
          <FavoriteAction favorite={track.favorite} trackId={track.id} />
          <DeliveryAction trackId={track.id} />
        </div>
      </header>

      <div className="mt-6 grid gap-5 xl:grid-cols-2">
        <ProviderProvenance track={track} />
        <ImportPanel track={track} />
        {track.library_import ? <MetadataForm track={track} /> : null}
        {track.library_import?.status === 'available' ? (
          <PlaylistOptions trackId={track.id} />
        ) : null}
        <Panel className="xl:col-span-2">
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-ink-3">
            Delivery summary
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {track.delivery_summary.total === 0 ? (
              <p className="text-sm text-ink-3">No deliveries recorded.</p>
            ) : (
              <>
                <StatusPill status="neutral" tone="neutral">
                  {track.delivery_summary.total} total
                </StatusPill>
                {track.delivery_summary.pending > 0 ? (
                  <StatusPill status="pending">{track.delivery_summary.pending} pending</StatusPill>
                ) : null}
                {track.delivery_summary.delivering > 0 ? (
                  <StatusPill status="delivering">
                    {track.delivery_summary.delivering} delivering
                  </StatusPill>
                ) : null}
                {track.delivery_summary.succeeded > 0 ? (
                  <StatusPill status="succeeded">
                    {track.delivery_summary.succeeded} succeeded
                  </StatusPill>
                ) : null}
                {track.delivery_summary.failed > 0 ? (
                  <StatusPill status="failed">{track.delivery_summary.failed} failed</StatusPill>
                ) : null}
              </>
            )}
          </div>
        </Panel>
      </div>
    </article>
  );
}
